<template>
  <BlockFieldset :class="`project-${protocol.id}-approval`">
    <div class="verification-flex m-1">
      <VerificationState
        :project="project"
        :verificationStateType="protocol.approval.stateType"
        :readonly="isStateReadOnly"
        :changeState="changeState"
        :canDecide="canDecide"
      />
      <Card v-if="noteParent">
        <template #title>{{ $t(protocol.approval.noteLabelKey) }} </template>
        <template #content>
          <b-form-textarea
            :value="noteParent[protocol.approval.note.field]"
            placeholder="..."
            rows="4"
            max-rows="4"
            @input="setNote"
          ></b-form-textarea>
        </template>
      </Card>
    </div>

    <div v-if="hasSaveButton" class="text-right mt-2">
      <b-button variant="bg" :disabled="isSaving" @click="save">
        {{ $t('saveNamed', { name: $t(protocol.approval.saveLabelKey) }) }}
      </b-button>
    </div>
  </BlockFieldset>
</template>

<script>
import { mapGetters } from 'vuex';
import Card from 'primevue/card';
import BlockFieldset from '@/components/Access/BlockFieldset.vue';
import VerificationState from '@/components/VerificationState.vue';
import { AccessBlockMixin } from '@/mixins/accessBlock';
import { BlockSaveMixin } from '@/mixins/blockSave';

/** Number of the unsaved project (ProjectEdit `_new`). */
const NEW_PROJECT_NUMBER = '_new';

/**
 * Block "<Protokoll>: Freigabe" of a protocol tab (see feature page projekt-protokolle): approval
 * state, changed immediately, and its note with own save button. The note sits at
 * `protocol.approval.note` (below `parentPath`, or top-level when it is empty); a nested note is
 * shown only when its parent object exists. In Matrix-Authz the level decides every state change;
 * in Legacy-Authz `VerificationState` keeps its role rules, and guests keep writing the note as
 * before. Emits `dirty` and `saved`.
 */
export default {
  name: 'ProjectReportApproval',
  components: { BlockFieldset, Card, VerificationState },
  mixins: [AccessBlockMixin, BlockSaveMixin],
  props: {
    blockId: { type: String, required: true },
    project: { type: Object, required: true },
    protocol: { type: Object, required: true },
  },
  computed: {
    ...mapGetters('auth', ['isOrganizationRoleNavigationEnabled']),
    /** Object holding the note: the project itself, or its subdocument at `parentPath`. */
    noteParent() {
      const { parentPath } = this.protocol.approval.note;
      return parentPath ? this.project[parentPath] : this.project;
    },
    /** The approval input of the protocol (only the note); the state changes through its own action. */
    blockInput() {
      const { field } = this.protocol.approval.note;
      return { [field]: this.noteParent?.[field] ?? null };
    },
    isNewProject() {
      return this.project.number === NEW_PROJECT_NUMBER;
    },
    hasSaveButton() {
      return !this.isBlockReadOnly && !this.isNewProject;
    },
    /** The state mutation needs a stored project number. */
    isStateReadOnly() {
      return this.isBlockReadOnly || this.isNewProject;
    },
    /** Matrix-Authz: the block level decides; Legacy-Authz: null keeps the role rules. */
    canDecide() {
      return this.isOrganizationRoleNavigationEnabled ? !this.isBlockReadOnly : null;
    },
  },
  methods: {
    /** `$set`, so a note missing in the loaded block becomes reactive. */
    setNote(value) {
      this.$set(this.noteParent, this.protocol.approval.note.field, value);
    },
    saveBlockInput(input) {
      return this.$store.dispatch(this.protocol.approval.saveAction, {
        projectNumber: this.project.number,
        input,
      });
    },
    changeState(verificationState) {
      return this.$store.dispatch(this.protocol.approval.changeStateAction, {
        projectNumber: this.project.number,
        verificationState,
      });
    },
  },
};
</script>

<style scoped lang="scss">
.verification-flex {
  display: flex;
  gap: 1em;
  flex-flow: row wrap;
  flex-basis: 100%;
  justify-content: flex-start;
}

.verification-flex > div {
  flex: 1;
  min-width: 300px;
}
</style>
